import {Environment} from '@coolcolduk/enum';
import {mapStringToEnumValue, throwIfUndefined} from '@coolcolduk/util';

export enum EnvVar {
  MODE = 'MODE',
  SUBSCRIBE = 'VITE_SUBSCRIBE',
  STRIPE_PORTAL = 'VITE_STRIPE_PORTAL',
  CLARITY_PROJECT_ID = 'VITE_CLARITY_PROJECT_ID',
  GDRIVE_OAUTH_CLIENT_ID = 'VITE_GDRIVE_OAUTH_CLIENT_ID',
}

/**
 * Get an environment variable
 */
function getEnvVar(key: EnvVar): string | undefined {
  return import.meta.env[key];
}

/**
 * Get an environment variable as a boolean
 */
function getEnvVarBool(key: EnvVar, defaultValue: boolean): boolean {
  const value = getEnvVar(key);
  if (value === undefined) {
    return defaultValue;
  }
  return value === 'true';
}

// Compute mode once to reuse for boolean flags
const mode = throwIfUndefined(mapStringToEnumValue(Environment, getEnvVar(EnvVar.MODE) || ''), 'MODE is not set');

export const env = {
  // App Configuration
  siteTitle: 'Crafty Smile',
  siteUrl: 'https://app.craftysmile.com/',
  webUrl: 'https://craftysmile.com/',

  // Firestore Collections
  collection: {
    contactMessages: 'contactMessages',
    generationJobProject: 'generationJobProject',
    etsyBulkUpdateGoogleSheetLink: 'etsyBulkUpdateGoogleSheetLink',
  },

  // Token Refresh
  /**
   * The time in milliseconds to wait before refreshing the firebase token.
   */
  refreshTokenMs: 50 * 60 * 1000, // 50 minutes

  // Stripe
  defaultStripeRole: 'free',

  // Cookies & Privacy
  cookieConsentKey: 'cookie_consent_accepted',
  privacyTextLastUpdated: '28 June 2025',

  // Environment
  /** determines the environment the app is running in */
  mode,
  /** determines if the subscription feature is enabled */
  subscribe: getEnvVarBool(EnvVar.SUBSCRIBE, false),
  /** Microsoft Clarity project ID for user analytics */
  clarityProjectId: throwIfUndefined(getEnvVar(EnvVar.CLARITY_PROJECT_ID), 'CLARITY_PROJECT_ID is not set'),
  /** Google OAuth client ID for Drive/Sheets (link account, Picker) */
  gdriveOauthClientId: getEnvVar(EnvVar.GDRIVE_OAUTH_CLIENT_ID),
  /** determines if the app is running in development mode */
  isDevelopment: mode === Environment.DEV,
  /** determines if the app is running in production mode */
  isProduction: mode === Environment.PROD,
  /** determines if the app is running in staging mode */
  isStaging: mode === Environment.STAGE,
};
